import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Sparkles,
  HandHeart,
  Brain,
  Anchor,
  Compass,
  TrendingUp,
  Users,
  Wind,
  Sunrise,
  Leaf,
  Mountain,
  ChevronRight,
  Plus,
  Minus,
  Heart,
  Home as HomeIcon,
} from "lucide-react";
import LeadMagnet from "@/components/LeadMagnet";
import Testimonials from "@/components/Testimonials";
import TikTokFeed from "@/components/TikTokFeed";
import YouTubeFeed from "@/components/YouTubeFeed";
import SEO from "@/components/SEO";

const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";

const HERO_IMG = "https://images.unsplash.com/photo-1754037783933-c25ff9f68f87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB0aGVyYXB5JTIwb2ZmaWNlJTIwd2FybSUyMHN1bmxpZ2h0fGVufDB8fHx8MTc3ODg1MTczNHww&ixlib=rb-4.1.0&q=85";
const HANDS_IMG = "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/aeae3fbc16d5d58644a3f7af64679b3b75d25173e56b234801f6f4b79de53aeb.png";

const pillars = [
  { icon: HandHeart, name: "Compassion", desc: "A safe, nonjudgmental space where you are seen and understood.", deeper: "We meet you where you actually are — not where you think you should be. No fixing, no rushing." },
  { icon: Sparkles, name: "Confidence", desc: "Building trust in yourself and your ability to handle life.", deeper: "We rebuild the inner voice that knows. Therapy that grows your trust in your own judgment over time." },
  { icon: Brain, name: "Insight", desc: "Gaining clarity about patterns, emotions, and choices.", deeper: "Why you keep choosing the same partner, the same job, the same loop — and how it can change." },
  { icon: Anchor, name: "Resilience", desc: "Strengthening your ability to cope, adapt, and recover.", deeper: "Practical regulation skills your nervous system can use in real life — not just in session." },
  { icon: Compass, name: "Alignment", desc: "Living in ways that reflect your values and goals.", deeper: "Surface what you actually care about. Build a life that runs on your compass, not someone else's." },
  { icon: TrendingUp, name: "Growth", desc: "Intentional progress at your own pace.", deeper: "Real change is slow. We honor your timeline and protect it from the culture that wants you sprinting." },
  { icon: Users, name: "Belonging", desc: "Feeling connected, supported, and not alone.", deeper: "Belonging starts inside, then ripples outward. We work on both — together, in real time." },
];

const soundsLikeYou = [
  "You find yourself overthinking everything, replaying conversations, or second-guessing your decisions.",
  "You give so much to others but still feel unfulfilled or emotionally drained.",
  "Your relationships feel confusing, overwhelming, or like the same patterns keep repeating.",
  "You're tired of feeling like you're not enough — even when you're doing your best.",
  "You want change, but you're not sure where to start.",
];

const helpYou = [
  "Identify patterns that keep repeating in your life",
  "Build confidence and trust in yourself",
  "Improve communication and relationships",
  "Manage anxiety, overthinking, and emotional overwhelm",
  "Create real, lasting change — not temporary relief",
];

const seasons = [
  { key: "anxious", icon: Wind, label: "Anxious", sub: "Mind won't stop.", headline: "Therapy for an overthinking mind.", body: "Practical tools to slow the spiral, regulate your nervous system, and stop renting your brain to worry.", cta: { label: "Start with Individual Therapy", to: "/services" } },
  { key: "transition", icon: Sunrise, label: "In transition", sub: "Everything's shifting.", headline: "For seasons that don't have a script.", body: "Breakup, divorce, career pivot, identity shift, new chapter — we help you choose your own next step.", cta: { label: "Read on life transitions", to: "/blog" } },
  { key: "healing", icon: Leaf, label: "Healing", sub: "Working through it.", headline: "Slow, structured, real healing.", body: "After a breakup, betrayal, family rupture, or a season that broke you open — we build the floor back under your feet.", cta: { label: "Post-breakup life skills", to: "/blog/life-skills-after-a-breakup-rebuilding-your-routine-confidence-and-peace" } },
  { key: "growing", icon: Mountain, label: "Growing", sub: "Leveling up.", headline: "When you're ready to grow on purpose.", body: "Clinical supervision, life-skills coaching, identity-affirming work for neurodivergent adults — the next layer.", cta: { label: "See supervision + coaching", to: "/lcsw-supervision" } },
];

const previewServices = [
  { key: "individual", icon: Heart, title: "Individual Therapy", punch: "Anxiety, overthinking, life pivots.", detail: "1:1 support tailored to you. Anxiety, dating burnout, life transitions, identity work. Virtual sessions across Texas." },
  { key: "couples", icon: Users, title: "Couples Therapy", punch: "Communication. Trust. Reconnection.", detail: "Repair work for partners ready to do the actual work. Communication, repair after rupture, intimacy, shared values." },
  { key: "family", icon: HomeIcon, title: "Family Therapy", punch: "Healthier dynamics, real change.", detail: "For families navigating transition, conflict, neurodivergence, or generational patterns. We move at your pace." },
];

const SOFT_HOVER = "transition-all duration-200 hover:-translate-y-0.5";

export default function Home() {
  const [activeSeason, setActiveSeason] = useState("anxious");
  const [activePillar, setActivePillar] = useState(0);
  const [openService, setOpenService] = useState(null);
  const seasonObj = seasons.find((s) => s.key === activeSeason);
  const pillarObj = pillars[activePillar];

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Seven Pillars of Life Therapy",
    description: "Compassionate counseling and coaching in Texas. Anxiety, depression, ADHD, autism life skills, couples therapy, and LMSW → LCSW supervision.",
    url: "https://www.7pillarsoflife.com",
    areaServed: { "@type": "State", name: "Texas" },
    image: "https://www.7pillarsoflife.com/og-image.jpg",
    founder: { "@type": "Person", name: "Sophia Dinwiddie-Donald, MSSW, LCSW-S" },
    serviceType: ["Individual Therapy", "Couples Therapy", "Family Therapy", "Life Skills Coaching for Autistic Adults", "ADHD Coaching", "LMSW to LCSW Supervision"],
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Anxiety, ADHD & Autism Life-Skills Therapy in Texas"
        description="Compassionate counseling and coaching across Texas — individual, couples, family therapy, life skills for autistic adults, and LMSW → LCSW supervision. By Sophia Dinwiddie-Donald, LCSW-S."
        path="/"
        jsonLd={orgJsonLd}
      />

      {/* HERO — same warm photo, bolder typography */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <img src={HERO_IMG} alt="Warm therapy office" className="absolute inset-0 w-full h-full object-cover scale-105 animate-[kenburns_18s_ease-out_forwards]" />
        {/* softer translucent gradient — photo breathes through */}
        <div className="absolute inset-0 bg-gradient-to-tr from-forest/75 via-forest/45 to-forest/5" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/55 via-forest/15 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 w-full">
          <div className="max-w-4xl animate-fade-up">
            <p className="inline-flex items-center mb-6 bg-terracotta text-bone px-4 py-2 rounded-full font-bold uppercase tracking-[0.22em] text-sm md:text-base" style={{ textShadow: "none" }}>
              Seven Pillars of Life Therapy
            </p>
            <h1 className="font-display text-bone font-semibold text-6xl sm:text-7xl md:text-8xl lg:text-[110px] leading-[1.0] tracking-tight" style={{ textShadow: "0 2px 28px rgba(0,0,0,0.55)" }}>
              You don't have to keep feeling{" "}
              <em
                className="italic text-terracotta font-bold"
                style={{ textShadow: "0 2px 22px rgba(0,0,0,0.65), 0 0 38px rgba(126,104,170,0.85)" }}
              >
                stuck
              </em>.
            </h1>
            <p className="mt-8 text-xl md:text-2xl text-bone/95 max-w-2xl leading-relaxed" style={{ textShadow: "0 1px 16px rgba(0,0,0,0.55)" }}>
              We help adults work through anxiety, relationship struggles, perfectionism, and life transitions — so you can move forward with clarity, confidence, and purpose.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-schedule-btn"
                className={`inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-medium ${SOFT_HOVER}`}
              >
                Schedule a Consultation <ArrowRight size={18} strokeWidth={1.5} />
              </a>
              <a
                href="#season-picker"
                data-testid="hero-season-btn"
                className={`inline-flex items-center justify-center gap-2 bg-transparent border border-bone/60 text-bone hover:bg-bone hover:text-forest rounded-full px-8 py-4 font-medium ${SOFT_HOVER}`}
              >
                What's Your Season? <Sparkles size={16} strokeWidth={1.6} />
              </a>
              <Link
                to="/#free-guide"
                data-testid="hero-selfcheck-btn"
                className="inline-flex items-center justify-center gap-2 text-bone/85 hover:text-terracotta underline underline-offset-4 px-2 py-4 font-medium transition-colors"
              >
                Free 5-Day Guide
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SOUNDS LIKE YOU */}
      <section className="py-24 md:py-36 bg-bone" data-testid="sounds-like-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline mb-5">Begin Here</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Does this sound <em className="italic text-terracotta">like you?</em>
            </h2>
            <p className="mt-6 text-ink/70 leading-relaxed">
              You're not alone in feeling this way — and you're not broken. These patterns are signals, and they can change.
            </p>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-forest font-medium link-underline" data-testid="sounds-like-cta">
              Tell us where you're at <ArrowRight size={16} />
            </Link>
          </div>
          <ul className="lg:col-span-7 space-y-6">
            {soundsLikeYou.map((s, i) => (
              <li
                key={i}
                className="flex gap-5 items-start p-6 bg-surface rounded-2xl border border-sage/25 hover:border-terracotta/40 hover:-translate-y-0.5 transition-all duration-300"
              >
                <span className="font-display text-3xl text-terracotta font-semibold leading-none mt-1">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-lg text-ink/85 leading-relaxed">{s}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SEASON PICKER — NEW interactive pathway */}
      <section id="season-picker" className="py-24 md:py-32 bg-surface px-6 md:px-12" data-testid="home-season">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-12">
            <p className="overline mb-5">Choose what fits</p>
            <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-[1.05] tracking-tight">
              What season are <em className="italic text-terracotta">you in?</em>
            </h2>
            <p className="mt-5 text-ink/70 text-lg leading-relaxed">
              Pick the word that fits right now. We'll show you a starting point.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10">
            {seasons.map((s) => {
              const Icon = s.icon;
              const active = s.key === activeSeason;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActiveSeason(s.key)}
                  data-testid={`home-season-btn-${s.key}`}
                  className={`text-left p-5 md:p-6 rounded-2xl border transition-all duration-200 ${active ? "bg-forest text-bone border-forest shadow-lg" : "bg-bone text-forest border-sage/30 hover:border-terracotta hover:-translate-y-0.5"}`}
                >
                  <Icon size={22} strokeWidth={1.8} className={`mb-3 ${active ? "text-terracotta" : "text-forest"}`} />
                  <p className="font-medium tracking-wide">{s.label}</p>
                  <p className={`mt-1 text-xs ${active ? "text-bone/70" : "text-ink/55"}`}>{s.sub}</p>
                </button>
              );
            })}
          </div>

          {seasonObj && (
            <div
              key={seasonObj.key}
              data-testid={`home-season-result-${seasonObj.key}`}
              className="rounded-[2rem] bg-bone p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-center border border-sage/25"
              style={{ animation: "fadeIn 0.3s ease-out" }}
            >
              <div className="md:col-span-8">
                <p className="overline mb-3">For when you're feeling {seasonObj.label.toLowerCase()}</p>
                <h3 className="font-display text-3xl md:text-4xl text-forest font-medium leading-tight">{seasonObj.headline}</h3>
                <p className="mt-4 text-ink/75 text-lg leading-relaxed">{seasonObj.body}</p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <Link
                  to={seasonObj.cta.to}
                  data-testid={`home-season-cta-${seasonObj.key}`}
                  className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-6 py-4 font-medium ${SOFT_HOVER}`}
                >
                  {seasonObj.cta.label} <ChevronRight size={16} strokeWidth={1.8} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="py-24 md:py-32 bg-bone" data-testid="how-we-help-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="overline mb-5">How We Help You Move Forward</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              We don't just talk about change — <em className="italic text-terracotta">we help you create it.</em>
            </h2>
            <ul className="mt-10 space-y-5">
              {helpYou.map((h, i) => (
                <li key={i} className="flex items-start gap-4 group">
                  <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-forest group-hover:bg-terracotta text-bone shrink-0 transition-colors">
                    <Check size={14} strokeWidth={2} />
                  </span>
                  <span className="text-lg text-ink/85 leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/services"
              className={`mt-10 inline-flex items-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-8 py-4 font-medium ${SOFT_HOVER}`}
              data-testid="how-we-help-cta"
            >
              Explore our services <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative aspect-[5/6] rounded-2xl overflow-hidden border border-sage/30 group">
              <img
                src={HANDS_IMG}
                alt="Comforting hands"
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-bone/10 rounded-2xl pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* THE SEVEN PILLARS — INTERACTIVE */}
      <section className="py-24 md:py-36 bg-forest text-bone relative overflow-hidden" data-testid="pillars-section">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
          backgroundImage: "radial-gradient(#FAF7FC 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline text-terracotta mb-5">Our Foundation</p>
            <h2 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] tracking-tight">
              Seven pillars. <em className="italic text-terracotta">Tap to explore.</em>
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-10">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              const isActive = i === activePillar;
              return (
                <button
                  key={p.name}
                  type="button"
                  onClick={() => setActivePillar(i)}
                  data-testid={`pillar-${p.name.toLowerCase()}`}
                  className={`text-left px-5 py-6 rounded-2xl border transition-all duration-200 ${isActive ? "bg-bone text-forest border-bone shadow-xl" : "bg-forest/40 border-bone/15 hover:border-terracotta hover:bg-forest/60"}`}
                >
                  <p className={`font-display text-3xl font-semibold leading-none ${isActive ? "text-terracotta" : "text-terracotta/80"}`}>{String(i + 1).padStart(2, "0")}</p>
                  <Icon size={20} strokeWidth={1.8} className={`my-3 ${isActive ? "text-forest" : "text-bone/80"}`} />
                  <p className={`font-medium text-sm ${isActive ? "text-forest" : "text-bone"}`}>{p.name}</p>
                </button>
              );
            })}
          </div>

          {pillarObj && (
            <div
              key={pillarObj.name}
              data-testid={`pillar-detail-${pillarObj.name.toLowerCase()}`}
              className="max-w-3xl rounded-2xl bg-bone/5 border border-bone/15 p-7 md:p-10"
              style={{ animation: "fadeIn 0.3s ease-out" }}
            >
              <p className="overline text-terracotta mb-3">Pillar {String(activePillar + 1).padStart(2, "0")} — {pillarObj.name}</p>
              <p className="font-display text-xl md:text-2xl text-bone leading-snug">{pillarObj.deeper}</p>
              <p className="mt-4 text-bone/65 leading-relaxed">{pillarObj.desc}</p>
            </div>
          )}

          <div className="mt-12">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pillars-book-btn"
              className={`inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-7 py-3.5 font-medium ${SOFT_HOVER}`}
            >
              Book a free consultation <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET */}
      <LeadMagnet />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* SERVICES PREVIEW — EXPANDABLE */}
      <section className="py-24 md:py-32 bg-bone" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline mb-5">What we offer</p>
              <h2 className="font-display text-4xl md:text-5xl text-forest font-medium tracking-tight max-w-xl">
                Therapy that meets you <em className="italic text-terracotta">where you are.</em>
              </h2>
              <p className="mt-5 text-ink/70 text-base md:text-lg">Tap any card to read more or book.</p>
            </div>
            <Link to="/services" className="text-forest font-medium link-underline" data-testid="services-view-all">
              View all services →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {previewServices.map((s) => {
              const Icon = s.icon;
              const expanded = openService === s.key;
              return (
                <article
                  key={s.key}
                  data-testid={`service-card-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                  className={`group bg-surface border border-sage/25 rounded-2xl p-8 hover:border-terracotta/50 hover:shadow-xl ${SOFT_HOVER}`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenService(expanded ? null : s.key)}
                    data-testid={`service-toggle-${s.key}`}
                    className="text-left w-full"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-forest/5 group-hover:bg-terracotta group-hover:text-bone text-forest mb-5 transition-colors">
                      <Icon size={22} strokeWidth={1.6} />
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-2xl text-forest font-medium group-hover:text-terracotta transition-colors">{s.title}</h3>
                      <span className={`shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-forest/20 text-forest/70 group-hover:border-terracotta group-hover:text-terracotta transition-colors`}>
                        {expanded ? <Minus size={14} strokeWidth={2} /> : <Plus size={14} strokeWidth={2} />}
                      </span>
                    </div>
                    <p className="mt-3 text-ink/70 leading-relaxed">{s.punch}</p>
                  </button>
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${expanded ? "grid-rows-[1fr] mt-5" : "grid-rows-[0fr]"}`}>
                    <div className="overflow-hidden">
                      <p className="text-ink/75 leading-relaxed text-base">{s.detail}</p>
                      <div className="mt-5 flex flex-wrap gap-3">
                        <Link
                          to="/services"
                          data-testid={`service-learn-${s.key}`}
                          className="inline-flex items-center gap-2 text-forest font-medium link-underline text-sm"
                        >
                          Learn more <ArrowRight size={14} />
                        </Link>
                        <a
                          href={BOOKING_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`service-book-${s.key}`}
                          className="inline-flex items-center gap-2 bg-forest hover:bg-terracotta text-bone rounded-full px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors"
                        >
                          Book a Consult
                        </a>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* TIKTOK FEED */}
      <TikTokFeed />

      {/* YOUTUBE FEED + LIVE BANNER */}
      <YouTubeFeed />

      {/* CLOSING CTA — bolder, like Explore */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-surface" data-testid="closing-cta">
        <div className="max-w-5xl mx-auto relative">
          <div className="rounded-[2.5rem] bg-terracotta text-bone p-12 md:p-20 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-bone/25" />
            <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full border border-bone/25" />
            <div className="absolute top-1/2 -right-8 w-32 h-32 rounded-full bg-bone/10" />
            <div className="relative">
              <p className="overline text-bone/80 mb-5">Begin Today</p>
              <h2 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] tracking-tight max-w-2xl">
                A first step toward feeling <em className="italic">like yourself again.</em>
              </h2>
              <p className="mt-6 text-lg md:text-xl text-bone/90 max-w-xl leading-relaxed">
                Schedule a free 15-minute consultation. No pressure — just a conversation to see if we're the right fit.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="closing-book-btn"
                  className={`inline-flex items-center gap-2 bg-bone text-forest hover:bg-forest hover:text-bone rounded-full px-8 py-4 font-medium ${SOFT_HOVER}`}
                >
                  Schedule Consultation <ArrowRight size={18} />
                </a>
                <Link
                  to="/contact"
                  data-testid="closing-contact-btn"
                  className={`inline-flex items-center gap-2 bg-transparent border border-bone text-bone hover:bg-bone hover:text-forest rounded-full px-8 py-4 font-medium ${SOFT_HOVER}`}
                >
                  Send a Message
                </Link>
                <Link
                  to="/blog"
                  data-testid="closing-blog-btn"
                  className="inline-flex items-center gap-2 text-bone hover:underline underline-offset-4 px-2 py-4 font-medium transition-colors"
                >
                  Read the blog <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes kenburns { 0% { transform: scale(1.05); } 100% { transform: scale(1.12); } }
      `}</style>
    </div>
  );
}
